import * as React from "react";
import { Field, FieldProps } from "formik";
import { Form, Checkbox, Input, TextArea, Label, FormField } from "@/components/bp/bp-ui";
import * as yup from "yup";
import { ParameterTypes, RunParameterModel } from "@app/api2/api";

export type ParameterFieldProps = {
    parameter: RunParameterModel;
    disabled?: boolean;
    name?: string; // optional override for Formik field name (defaults to parameter.parameter)
    onValueChange?: (p: RunParameterModel, v: any) => void;
}

// Renders an appropriate Formik field for a single RunParameterModel
export const ParameterField: React.FC<ParameterFieldProps> = ({ parameter, disabled, name, onValueChange }) => {
    const fieldName = name || parameter.parameter;

    // For booleans the backend uses "1"/"0" strings in many places; keep representation as string to match existing code
    if (parameter.parameterType === ParameterTypes.Boolean) {
        return (
            <Field name={fieldName}>
                {({ field, form }: FieldProps) => {
                    const checked = isParameterTruthy(field.value);
                    const meta = form.getFieldMeta(fieldName);
                    return (
                        <FormField error={meta && meta.touched && !!meta.error}>
                            <Checkbox
                                checked={checked}
                                disabled={disabled}
                                onChange={async (_, data) => {
                                    // write back as "1"/"0" to keep parity with existing codebase
                                    const v = data.checked ? "1" : "0";
                                    const errors = await form.setFieldValue(fieldName, v, true);
                                    // console.log('Set field value result:', errors);
                                    // if (isNullEmptyObject(errors) && typeof onValueChange === 'function')
                                        onValueChange(parameter, v);
                                }}
                            />
                            {meta && meta.touched && meta.error && <Label basic color="red" pointing>{String(meta.error)}</Label>}
                        </FormField>
                    );
                }}
            </Field>
        );
    }

    // For JSON and String types render a TextArea when multi-line is likely, otherwise an Input
    if (parameter.parameterType === ParameterTypes.Json || (parameter.name && parameter.name.toLowerCase().includes("json"))) {
        return (
            <Field name={fieldName}>
                {({ field, form }: FieldProps) => {
                    const meta = form.getFieldMeta(fieldName);
                    const hasError = !!(meta && meta.touched && meta.error);
                    return (
                        <FormField error={hasError}>
                            <TextArea {...field} disabled={disabled} className={hasError ? 'error' : ''} onChange={async (_, data) => {
                                const errors = await form.setFieldValue(fieldName, data.value, true);

                                // if (isNullEmptyObject(errors) && typeof onValueChange === 'function')
                                    onValueChange(parameter, data.value);
                            }} />
                            {hasError && <Label basic color="red" pointing>{String(meta.error)}</Label>}
                        </FormField>
                    );
                }}
            </Field>
        );
    }

    // Numbers and integers - use Input of type number
    if (parameter.parameterType === ParameterTypes.Integer || parameter.parameterType === ParameterTypes.Number) {
        return (
            <Field name={fieldName}>
                {({ field, form }: FieldProps) => {
                    const meta = form.getFieldMeta(fieldName);
                    const hasError = !!(meta && meta.touched && meta.error);
                    return (
                        <FormField error={hasError}>
                            <Input
                                {...field}
                                disabled={disabled}
                                type="number"
                                error={hasError}
                                onChange={async (_, data) => {
                                    const errors = await form.setFieldValue(fieldName, data.value, true);

                                    // if (isNullEmptyObject(errors) && typeof onValueChange === 'function')
                                        onValueChange(parameter, data.value);
                                }}
                            />
                            {hasError && <Label basic color="red" pointing>{String(meta.error)}</Label>}
                        </FormField>
                    );
                }}
            </Field>
        );
    }

    // Default: simple text input
    return (
        <Field name={fieldName}>
            {({ field, form }: FieldProps) => {
                const meta = form.getFieldMeta(fieldName);
                const hasError = !!(meta && meta.touched && meta.error);
                return (
                    <FormField error={hasError}>
                        <Input {...field} disabled={disabled} error={hasError} onChange={async (_, data) => {
                            const errors = await form.setFieldValue(fieldName, data.value, true);

                            // if (isNullEmptyObject(errors) && typeof onValueChange === 'function')
                                onValueChange(parameter, data.value);
                        }} />
                        {hasError && <Label basic color="red" pointing>{String(meta.error)}</Label>}
                    </FormField>
                );
            }}
        </Field>
    );
}

export function isParameterTruthy(isChecked: any) {
    return isChecked === "1" || isChecked === 1 || isChecked === true || (isChecked instanceof String && isChecked.toLowerCase() === "true");
}

// Helper: build a Yup schema for an array of RunParameterModel
export function buildParametersValidationSchema(parameters: RunParameterModel[] = []) {
    const shape: { [key: string]: yup.BaseSchema } = {};

    for (const p of parameters) {
        let schema: yup.BaseSchema;
        switch (p.parameterType) {
            case ParameterTypes.String:
            case ParameterTypes.Json:
                schema = yup.string();
                break;
            case ParameterTypes.Integer:
                schema = yup.number().integer();
                break;
            case ParameterTypes.Number:
                schema = yup.number();
                break;
            case ParameterTypes.Boolean:
                // represent boolean as string "1"/"0" or boolean
                schema = yup.mixed().test('is-boolean-1-0', 'Invalid boolean', (v) => v === "1" || v === "true" || v === "false" || v === 1 || v === true || v === "0" || v === false || typeof v === 'boolean' || v === 1 || v === 0 || v === null || v === undefined);
                break;
            default:
                schema = yup.mixed();
        }

        // min/max only make sense for number types
        if ((p.parameterType === ParameterTypes.Integer || p.parameterType === ParameterTypes.Number) && p.minValue > 0) {
            schema = (schema as yup.NumberSchema).min(p.minValue);
        }
        if ((p.parameterType === ParameterTypes.Integer || p.parameterType === ParameterTypes.Number) && p.maxValue > 0) {
            schema = (schema as yup.NumberSchema).max(p.maxValue);
        }

        if (p.required) {
            // special handling for showIfChecked - if present the field is conditionally required
            if (p.showIfChecked) {
                // create a conditional schema that checks the referenced field(s) at runtime
                schema = schema.when(p.showIfChecked, (isChecked: any, sch: any) => {
                    // treat the dependent field as enabled only when it's "1" (or boolean/number truthy equivalent)
                    const enabled = isParameterTruthy(isChecked);
                    return enabled ? sch.nullable().required() : sch.nullable(true);
                });
            } else {
                schema = schema.nullable().required();
            }
        } else {
            schema = schema.nullable(true);
        }

        // attach a label if available
        schema = schema.label(p.name || p.parameter);

        shape[p.parameter] = schema;
    }

    return yup.object().shape(shape);
}

export default ParameterField;

